/// Auto-generated with ScanbotSDKCodegenV3. Modifications will be overwritten.
/// Generated from core/schemas/DocumentQualityAnalyzerTypes.yaml
import { ToJsonConfiguration } from '../common/JsonSerializationTypes';
import { ImageRef } from '../image/image';
/** @hidden */
export const DocumentQualityValues = [
    'VERY_POOR',
    'POOR',
    'REASONABLE',
    'GOOD',
    'EXCELLENT',
];
/** @hidden */
export const DocumentQualityAssessmentValues = [
    'ACCEPTABLE',
    'UNACCEPTABLE',
    'UNCERTAIN',
];
/**
Point in the quality-number of symbols space to separate quality levels.
*/
export class DocumentQualityThreshold {
    /** @param source {@displayType `DeepPartial<DocumentQualityThreshold>`} */
    constructor(source = {}) {
        if (source.symbolQuality !== undefined) {
            this.symbolQuality = source.symbolQuality;
        }
        else {
            throw new Error('symbolQuality must be present in constructor argument');
        }
        if (source.symbolRatio !== undefined) {
            this.symbolRatio = source.symbolRatio;
        }
        else {
            throw new Error('symbolRatio must be present in constructor argument');
        }
    }
}
/**
Configuration for processing image by tiles.
This can be used to make the document quality analyzer processing faster with lower memory consumption,
but prediction can be less accurate.
*/
export class ProcessByTileConfiguration {
    /** @param source {@displayType `DeepPartial<ProcessByTileConfiguration>`} */
    constructor(source = {}) {
        /**
          Enable processing by tiles.
          
          Default is false
          */
        this.enabled = false;
        /**
          At least this fraction of the image will be processed, range is from 0 to 1.
          Decreasing this value below 1.0 can lead to faster processing but may result in
          less accurate prediction, as only a subset of the image tiles will be analyzed.
          
          Default is 1
          */
        this.minProcessedFraction = 1.0;
        /**
          At most this fraction of the image will be processed, range is from 0 to 1.
          Decreasing this value below 1.0 can lead to faster processing but may result in
          less accurate prediction, as only a subset of the image tiles will be analyzed.
          
          Default is 1
          */
        this.maxProcessedFraction = 1.0;
        /**
          If this number of symbols is found and minProcessedFraction of the image is processed, the processing stops.
          
          Default is 100
          */
        this.minNumberOfRequiredSymbols = 100;
        /**
          Image will be processed in tiles of this size; will be ignored if image is small.
          
          Default is 300
          */
        this.tileSize = 300;
        if (source.enabled !== undefined) {
            this.enabled = source.enabled;
        }
        if (source.minProcessedFraction !== undefined) {
            this.minProcessedFraction = source.minProcessedFraction;
        }
        if (source.maxProcessedFraction !== undefined) {
            this.maxProcessedFraction = source.maxProcessedFraction;
        }
        if (source.minNumberOfRequiredSymbols !== undefined) {
            this.minNumberOfRequiredSymbols = source.minNumberOfRequiredSymbols;
        }
        if (source.tileSize !== undefined) {
            this.tileSize = source.tileSize;
        }
    }
}
/** @hidden */
export const DocumentQualityAnalyzerCompatibilityModeValues = [
    'V8',
];
/**
Configuration for the compatibility mode of the Document Quality Analyzer.
In this mode, the analyzer will return results that are identical to older versions of the SDK.
*/
export class DocumentQualityAnalyzerCompatibilityModeConfiguration {
    /** @param source {@displayType `DeepPartial<DocumentQualityAnalyzerCompatibilityModeConfiguration>`} */
    constructor(source = {}) {
        /**
          Compatibility mode to use.
          
          Default is V8
          */
        this.mode = 'V8';
        /**
          This setting describes how the Document Quality Analyzer in v8 compatibility mode will behave.
          Minimum compatibility-mode quality that gets converted to DocumentQualityAssessment.ACCEPTABLE.
          Compatibility-mode quality levels equal to or higher than `minimumAcceptableQuality` result in an ACCEPTABLE quality assessment.
          
          Default is REASONABLE
          */
        this.minimumAcceptableQuality = 'REASONABLE';
        /**
          Minimum compatibility-mode quality that gets converted to DocumentQualityAssessment.UNCERTAIN.
          Compatibility-mode quality levels between `minimumUncertainQuality` (inclusive) and `minimumAcceptableQuality` (exclusive) result in an UNCERTAIN quality assessment.
          
          Default is POOR
          */
        this.minimumUncertainQuality = 'POOR';
        /**
          Inspect small text (typically less than 12 pixels height) when analyzing the document quality.
          If true, processing will be slower, but when the quality issue with a document is the low resolution of the text,
          then the issue will be caught.
          
          Default is false
          */
        this.inspectSmallText = false;
        if (source.mode !== undefined) {
            this.mode = source.mode;
        }
        if (source.minimumAcceptableQuality !== undefined) {
            this.minimumAcceptableQuality = source.minimumAcceptableQuality;
        }
        if (source.minimumUncertainQuality !== undefined) {
            this.minimumUncertainQuality = source.minimumUncertainQuality;
        }
        if (source.inspectSmallText !== undefined) {
            this.inspectSmallText = source.inspectSmallText;
        }
    }
    serialize(config = new ToJsonConfiguration()) {
        return {
            mode: this.mode,
            minimumAcceptableQuality: this.minimumAcceptableQuality,
            minimumUncertainQuality: this.minimumUncertainQuality,
            inspectSmallText: this.inspectSmallText,
        };
    }
}
/**
Document Quality Analyzer configuration

The document quality analysis of a document can be slow on low end devices.
To speed up the analysis, only parts of the the document can be analyzed.
This can yield faster results but may result in less accurate predictions.
The configuration parameters `minProcessedFraction` and `maxProcessedFraction` define a range of the image that will be analyzed.
In that range, `minNumberOfRequiredSymbols` and `minRequiredOrientationConfidence` define under which conditions the analysis will stop early.
*/
export class DocumentQualityAnalyzerConfiguration {
    /** @param source {@displayType `DeepPartial<DocumentQualityAnalyzerConfiguration>`} */
    constructor(source = {}) {
        /**
          Document quality analysis model to use.
          If empty, the built-in model is used which provides reasonable quality estimates for a wide variety of documents.
          If the built-in model is not a good fit for your use case, consider creating a model based on your own documents and use case.
          
          To create a fine-tuned quality analysis model, follow the documentation of our DoQA model configurator tool.
          You can find it here: https://github.com/doo/scanbot-sdk-doqa-configurator.
          
          Default is ""
          */
        this.qualityAnalysisModel = '';
        /**
          There are documents for which the quality analysis model is uncertain, meaning that the model is not sure if the document is acceptable or not.
          E.g., the model may report a document quality to be `ACCEPTABLE` with 60% confidence.
          Depending on the value of this threshold, the document in this case will be reported as `ACCEPTABLE` or `UNCERTAIN`.
          The default value of 0.5 will typically lead to a good balance between the number of documents reported as `ACCEPTABLE` and `UNCERTAIN`.
          A lower value will lead to fewer UNCERTAIN documents, but unacceptable documents will more often be reported as `ACCEPTABLE` (false positives).
          A higher value will lead to more borderline documents being reported as UNCERTAIN, but unacceptable documents will be reported as `ACCEPTABLE` less often.
          If 0, no documents with a predicted quality of `ACCEPTABLE` will be reported as `UNCERTAIN`, regardless of the model's confidence, unless the document has very few symbols (see `minEstimatedNumberOfSymbolsForDocument`).
          If 1, almost all documents with a predicted quality of `ACCEPTABLE` will be reported as `UNCERTAIN`.
          Must be in the range of [0, 1].
          
          Default is 0.5
          */
        this.qualityAcceptableUncertainThreshold = 0.5;
        /**
          Same as `qualityAcceptableUncertainThreshold`, but for documents that are predicted to be `UNACCEPTABLE` by the model.
          
          Default is 0.5
          */
        this.qualityUnacceptableUncertainThreshold = 0.5;
        /**
          Deprecated, please use `qualityAnalysisModel` instead.
          Quality thresholds to separate quality levels.
          */
        this.qualityLevelThresholds = [
            new DocumentQualityThreshold({ symbolQuality: 0.5, symbolRatio: 0.5 }),
            new DocumentQualityThreshold({ symbolQuality: 0.7, symbolRatio: 0.3 }),
            new DocumentQualityThreshold({ symbolQuality: 0.85, symbolRatio: 0.3 }),
            new DocumentQualityThreshold({ symbolQuality: 0.9, symbolRatio: 0.1 }),
        ];
        /**
          Deprecated, please use `qualityAnalysisModel` instead.
          Quality levels.
          */
        this.qualityLevelIndices = ['VERY_POOR', 'POOR', 'REASONABLE', 'GOOD', 'EXCELLENT'];
        /**
          Runs the Document Quality Analyzer on the listed inputScales. Only the best result will be returned.
          Typical range of the text height, which model can work with, is 5-40 pixels.
          10 pixels height is recommended.
          Use this option if documents with smaller or bigger texts are expected.
          For example, if 80 pixels text height is expected, scale should be 1/8.
          Not compatible with maxImageSize. If not [1] maxImageSize should be less or equal 0.
          */
        this.inputScales = [1.0];
        /**
          If inputScale from inputScales is smaller or equal than the threshold minProcessedFraction and maxProcessedFraction will be ignored, and the entire image will be processed.
          
          Default is 0.25
          */
        this.inputScaleThresholdToProcessEntireImage = 0.25;
        /**
          Maximum image size in pixels, if image is bigger, it will be resized. If less or equal 0 will be ignored.
          
          Default is 2000
          */
        this.maxImageSize = 2000;
        /**
          If a document's estimated number of symbols is less than this value, document quality will be reported as `UNCERTAIN`.
          In that case, the deprecated result fields `documentFound` and`qualityLevel` will be set to false and empty, respectively.
          
          Default is 20
          */
        this.minEstimatedNumberOfSymbolsForDocument = 20;
        /**
          Configuration for processing image by tiles.
          */
        this.processByTileConfiguration = new ProcessByTileConfiguration({});
        /**
          If detectOrientation is True, minNumberOfRequiredSymbols is found, and orientationConfidence is bigger than this threshold, the processing stops.
          If the maxProcessedFraction part of the image is processed and orientationConfidence is lower than the threshold, the returned orientation will be 0.
          
          Default is 0.9
          */
        this.minRequiredOrientationConfidence = 0.9;
        /**
          If true, will return the quality heatmap as a part of the result.
          In this case, the entire image will be processed and the minProcessedFraction will be ignored.
          Useful for debugging situations where the returned quality doesn't appear to match the perceived quality.
          
          Default is false
          */
        this.returnQualityHeatmap = false;
        /**
          Compatibility mode for the Document Quality Analyzer.
          If set, the analyzer will return results that are identical to older versions of the SDK.
          */
        this.compatibilityMode = null;
        if (source.qualityAnalysisModel !== undefined) {
            this.qualityAnalysisModel = source.qualityAnalysisModel;
        }
        if (source.qualityAcceptableUncertainThreshold !== undefined) {
            this.qualityAcceptableUncertainThreshold = source.qualityAcceptableUncertainThreshold;
        }
        if (source.qualityUnacceptableUncertainThreshold !== undefined) {
            this.qualityUnacceptableUncertainThreshold = source.qualityUnacceptableUncertainThreshold;
        }
        if (source.qualityLevelThresholds !== undefined) {
            this.qualityLevelThresholds = source.qualityLevelThresholds.map((it) => {
                return new DocumentQualityThreshold(it);
            });
        }
        if (source.qualityLevelIndices !== undefined) {
            this.qualityLevelIndices = source.qualityLevelIndices.map((it) => {
                return it;
            });
        }
        if (source.inputScales !== undefined) {
            this.inputScales = source.inputScales.map((it) => {
                return it;
            });
        }
        if (source.inputScaleThresholdToProcessEntireImage !== undefined) {
            this.inputScaleThresholdToProcessEntireImage = source.inputScaleThresholdToProcessEntireImage;
        }
        if (source.maxImageSize !== undefined) {
            this.maxImageSize = source.maxImageSize;
        }
        if (source.minEstimatedNumberOfSymbolsForDocument !== undefined) {
            this.minEstimatedNumberOfSymbolsForDocument = source.minEstimatedNumberOfSymbolsForDocument;
        }
        if (source.processByTileConfiguration !== undefined) {
            this.processByTileConfiguration = new ProcessByTileConfiguration(source.processByTileConfiguration);
        }
        if (source.minRequiredOrientationConfidence !== undefined) {
            this.minRequiredOrientationConfidence = source.minRequiredOrientationConfidence;
        }
        if (source.returnQualityHeatmap !== undefined) {
            this.returnQualityHeatmap = source.returnQualityHeatmap;
        }
        if (source.compatibilityMode !== undefined) {
            this.compatibilityMode =
                source.compatibilityMode != null
                    ? new DocumentQualityAnalyzerCompatibilityModeConfiguration(source.compatibilityMode)
                    : null;
        }
    }
}
/**
Character level annotation, contains information about the quality of a character in the document.
*/
export class CharacterLevelAnnotation {
    /** @param source {@displayType `DeepPartial<CharacterLevelAnnotation>`} */
    constructor(source = {}) {
        if (source.plotCenter !== undefined) {
            this.plotCenter = { x: source.plotCenter.x, y: source.plotCenter.y };
        }
        else {
            throw new Error('plotCenter must be present in constructor argument');
        }
        if (source.plotWidth !== undefined) {
            this.plotWidth = source.plotWidth;
        }
        else {
            throw new Error('plotWidth must be present in constructor argument');
        }
        if (source.plotHeight !== undefined) {
            this.plotHeight = source.plotHeight;
        }
        else {
            throw new Error('plotHeight must be present in constructor argument');
        }
        if (source.ocrability !== undefined) {
            this.ocrability = source.ocrability;
        }
        else {
            throw new Error('ocrability must be present in constructor argument');
        }
        if (source.contrast !== undefined) {
            this.contrast = source.contrast;
        }
        else {
            throw new Error('contrast must be present in constructor argument');
        }
        if (source.fontSize !== undefined) {
            this.fontSize = source.fontSize;
        }
        else {
            throw new Error('fontSize must be present in constructor argument');
        }
        if (source.orientation !== undefined) {
            this.orientation = source.orientation;
        }
        else {
            throw new Error('orientation must be present in constructor argument');
        }
        if (source.orientationNormalized !== undefined) {
            this.orientationNormalized = source.orientationNormalized;
        }
        else {
            throw new Error('orientationNormalized must be present in constructor argument');
        }
        if (source.orientationDeviation !== undefined) {
            this.orientationDeviation = source.orientationDeviation;
        }
        else {
            throw new Error('orientationDeviation must be present in constructor argument');
        }
    }
    serialize(config = new ToJsonConfiguration()) {
        return {
            plotCenter: this.plotCenter,
            plotWidth: this.plotWidth,
            plotHeight: this.plotHeight,
            ocrability: this.ocrability,
            contrast: this.contrast,
            fontSize: this.fontSize,
            orientation: this.orientation,
            orientationNormalized: this.orientationNormalized,
            orientationDeviation: this.orientationDeviation,
        };
    }
}
/**
Character level annotations, contains information about each character in the document.
*/
export class CharacterLevelAnnotations {
    /** @param source {@displayType `DeepPartial<CharacterLevelAnnotations>`} */
    constructor(source = {}) {
        if (source.annotations !== undefined) {
            this.annotations = source.annotations.map((it) => {
                return new CharacterLevelAnnotation(it);
            });
        }
        else {
            throw new Error('annotations must be present in constructor argument');
        }
        if (source.apiVersion !== undefined) {
            this.apiVersion = source.apiVersion;
        }
        else {
            throw new Error('apiVersion must be present in constructor argument');
        }
    }
    serialize(config = new ToJsonConfiguration()) {
        return {
            annotations: this.annotations.map((it) => {
                return it.serialize(config);
            }),
            apiVersion: this.apiVersion,
        };
    }
}
/**
Result of document quality analysis. It can be used to determine, e.g., if a document is good enough to be used for OCR processing.
*/
export class DocumentQualityAnalyzerResult {
    /** @param source {@displayType `DeepPartial<DocumentQualityAnalyzerResult>`} */
    constructor(source = {}) {
        /**
          Quality heatmap.
          Black marks areas of no detected text. Colors represent the quality of detected text,
          with red meaning the lowest quality and yellow and green meaning progressively higher quality.
          */
        this.qualityHeatmap = null;
        this._released = false;
        if (source.quality !== undefined) {
            this.quality = source.quality;
        }
        else {
            throw new Error('quality must be present in constructor argument');
        }
        if (source.documentFound !== undefined) {
            this.documentFound = source.documentFound;
        }
        else {
            throw new Error('documentFound must be present in constructor argument');
        }
        if (source.qualityLevel !== undefined) {
            this.qualityLevel =
                source.qualityLevel != null
                    ? DocumentQualityValues.includes(source.qualityLevel)
                        ? source.qualityLevel
                        : null
                    : null;
        }
        else {
            throw new Error('qualityLevel must be present in constructor argument');
        }
        if (source.orientation !== undefined) {
            this.orientation = source.orientation;
        }
        else {
            throw new Error('orientation must be present in constructor argument');
        }
        if (source.orientationConfidence !== undefined) {
            this.orientationConfidence = source.orientationConfidence;
        }
        else {
            throw new Error('orientationConfidence must be present in constructor argument');
        }
        if (source.cumulativeQualityHistogram !== undefined) {
            this.cumulativeQualityHistogram = source.cumulativeQualityHistogram.map((it) => {
                return it;
            });
        }
        else {
            throw new Error('cumulativeQualityHistogram must be present in constructor argument');
        }
        if (source.qualityHeatmap !== undefined) {
            this.qualityHeatmap = source.qualityHeatmap != null ? ImageRef.from(source.qualityHeatmap) : null;
        }
        if (source.bestInputScale !== undefined) {
            this.bestInputScale = source.bestInputScale;
        }
        else {
            throw new Error('bestInputScale must be present in constructor argument');
        }
    }
    async serialize(config = new ToJsonConfiguration()) {
        return {
            quality: this.quality,
            documentFound: this.documentFound,
            qualityLevel: this.qualityLevel != null ? this.qualityLevel : null,
            orientation: this.orientation,
            orientationConfidence: this.orientationConfidence,
            cumulativeQualityHistogram: this.cumulativeQualityHistogram.map((it) => {
                return it;
            }),
            qualityHeatmap: config.serializeImages
                ? this.qualityHeatmap != null
                    ? await this.qualityHeatmap.serialize(config.imageSerializationMode)
                    : null
                : undefined,
            bestInputScale: this.bestInputScale,
        };
    }
    release() {
        if (this._released) {
            return;
        }
        {
            if (this.qualityHeatmap != null) {
                this.qualityHeatmap.release();
            }
        }
        this._released = true;
    }
    async encodeImages() {
        if (this.qualityHeatmap != null) {
            await this.qualityHeatmap.encodeInPlace();
        }
    }
}
//# sourceMappingURL=DocumentQualityAnalyzerTypes.js.map